import { useQuery } from '@apollo/client';
import React, { useState } from 'react';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Combobox,
  ComboboxDropdown,
  ComboboxEmpty,
  ComboboxOption,
  ComboboxSelectButton,
  Dialog,
  DialogContent,
  DialogFooter,
  Heading,
  Icon,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useSummary,
} from '@eluve/frontend-appointment-hooks';
import { useAppointmentActivity } from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';
import {
  useIsEluveAdmin,
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useDialog } from '@eluve/utility-hooks';

import { BundleTemplateSelector } from './BundleTemplateSelector';
import { useRegenerateSummary } from './useRegenerateSummary';

const getLlmModelArgsQuery = graphql(`
  query getLlmModelArgsForRegenerate {
    llmModelArgs(
      where: { isActive: { _eq: true } }
      orderBy: { modelType: ASC }
    ) {
      __typename
      id
      name
      modelType
      args
    }
  }
`);

export interface RegenerateSummaryButtonProps {}

export const RegenerateSummaryButton: React.FC<
  RegenerateSummaryButtonProps
> = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const logger = useNamedLogger('RegenerateSummaryButton');
  const { isDialogOpen, toggleDialog, setIsDialogOpen } = useDialog();

  const userId = useUserIdFromSession();
  const tenantId = useTenantIdFromParams();
  const currentSummary = useSummary();
  const appointmentId = useAppointmentId();

  const { regenerateSummary, promptModelArgId, setPromptModelArgId } =
    useRegenerateSummary();

  const isEluveAdmin = useIsEluveAdmin();

  const captureEvent = useCaptureEvent({
    tenantId,
    appointmentId,
    llmOutputId: currentSummary.llmOutputId,
  });

  const { isSummarizationInProgress, isTranscriptionInProgress } =
    useAppointmentActivity();

  const regenerationDisabled =
    isSummarizationInProgress || isTranscriptionInProgress;

  const handleRegenerate = async () => {
    toggleDialog();

    logger.info('Regenerate Summary Button Clicked', {
      userId,
      tenantId,
      appointmentId,
      currrentSummaryLlmOutputId: currentSummary.llmOutputId,
    });

    captureEvent('user_regenerated_note_summary', {
      llmOutputId: currentSummary.llmOutputId,
    });

    await regenerateSummary(
      () => {
        toast.success(t('appointment.regenerate.title'), {
          description: t('appointment.regenerate.successDescription'),
        });
      },
      () => {
        toast.error(t('appointment.regenerate.errorTitle'), {
          description: t('appointment.regenerate.errorDescription'),
        });
      },
    );
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={regenerationDisabled}
        onClick={() => {
          captureEvent('user_opened_regenerate_note_summary_dialog');
          toggleDialog();
        }}
      >
        <Icon name="RefreshCcw" size="auto" />
        {t('appointment.regenerate.button')}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent size="lg">
          <Stack gap={5} className="p-4">
            <Heading size="3xl">{t('appointment.regenerate.title')}</Heading>
            <Text>{t('appointment.regenerate.overwriteWarning')}</Text>
            <div className="h-[0.5px] w-full justify-between rounded-sm bg-border-secondary" />

            <Stack gap={1} align="stretch">
              <Text>{t('appointment.regenerate.templateLabel')}</Text>
              <Stack
                className="rounded-b-xl"
                direction="horizontal"
                justify="between"
                align="stretch"
              >
                <BundleTemplateSelector usesPortal={false} />
              </Stack>
            </Stack>
            {isEluveAdmin && (
              <AdminModelSelector
                selectedModelArgId={promptModelArgId}
                onModelArgChange={setPromptModelArgId}
              />
            )}
          </Stack>
          <DialogFooter>
            <Button
              variant="ghost"
              className="w-fit bg-bg-quaternary hover:bg-border-primary"
              onClick={toggleDialog}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={handleRegenerate} className="w-fit">
              <Icon name="RefreshCcw" className="pr-1" />
              {t('appointment.regenerate.button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const AdminModelSelector: React.FC<{
  selectedModelArgId: string | undefined;
  onModelArgChange: (id: string | undefined) => void;
}> = ({ selectedModelArgId, onModelArgChange }) => {
  const { data } = useQuery(getLlmModelArgsQuery, {
    context: eluveAdminHasuraContext,
  });
  const [isOpen, setIsOpen] = useState(false);

  const modelArgs = data?.llmModelArgs ?? [];
  const selectedModelArg = modelArgs.find((m) => m.id === selectedModelArgId);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1 text-sm text-text-tertiary hover:text-text-primary"
        >
          <Icon
            name="ChevronDown"
            className={`transition-transform ${isOpen ? '' : '-rotate-90'}`}
          />
          Advanced Options
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        <Stack gap={1} align="stretch">
          <Text size="xs" color="secondary">
            Model Override
          </Text>
          <Combobox>
            <ComboboxSelectButton className="w-full">
              {selectedModelArg
                ? `${selectedModelArg.modelType}${selectedModelArg.name ? ` - ${selectedModelArg.name}` : ''}`
                : 'Default (from template)'}
            </ComboboxSelectButton>
            <ComboboxDropdown searchPlaceholder="Search models...">
              <ComboboxEmpty>No models found</ComboboxEmpty>
              <ComboboxOption
                onSelect={() => onModelArgChange(undefined)}
                searchValue="default template"
              >
                <Text size="md">Default (from template)</Text>
              </ComboboxOption>
              {modelArgs.map((arg) => {
                const args = arg.args as Record<string, unknown> | null;
                const model = args?.model as string | undefined;
                const temp = args?.temperature as number | undefined;
                const maxTokens = args?.max_tokens as number | undefined;
                const argDetails = [
                  model,
                  temp !== undefined ? `temp=${temp}` : null,
                  maxTokens !== undefined ? `max=${maxTokens}` : null,
                ]
                  .filter(Boolean)
                  .join(', ');

                return (
                  <ComboboxOption
                    key={arg.id}
                    onSelect={() => onModelArgChange(arg.id)}
                    searchValue={`${arg.modelType} ${arg.name ?? ''} ${model ?? ''} ${arg.id}`}
                  >
                    <Stack gap={0.5} align="stretch">
                      <Text size="md">
                        {arg.modelType}
                        {arg.name ? ` - ${arg.name}` : ''}
                      </Text>
                      {argDetails && (
                        <Text size="xs" color="tertiary">
                          {argDetails}
                        </Text>
                      )}
                    </Stack>
                  </ComboboxOption>
                );
              })}
            </ComboboxDropdown>
          </Combobox>
        </Stack>
      </CollapsibleContent>
    </Collapsible>
  );
};
